import { useEffect, useState } from 'react';
import moment from 'moment';
import { CalendarCustom } from '../../../components';
import { DATE_YMD_ISO, MSG } from '../../../core/configs/constants';
import './ApprovalConfirmModal.scss';

// Stable fallback so the "open" effect below does not re-run on every render when no list is passed.
const NO_CODES = [];

// Shared approval/rejection modal (承認 / 不承認).
// attendanceCodes: codes the GA may approve the learner with — the learner's current code (isCurrent)
// plus the other selectable codes of the group. Each carries coursePeriodDate: the period preset on
// the code (null = GA picks the start).
const ApprovalConfirmModal = ({ mode, student, attendanceCodes = NO_CODES, onCancel, onApprove, onReject }) => {
    const [codeId, setCodeId] = useState('');
    const [startDate, setStartDate] = useState('');
    const [startError, setStartError] = useState(false);
    const [codeError, setCodeError] = useState(false);

    // False when the learner's current code no longer exists (deleted on the CMS meanwhile): nothing is
    // preselected then, so the GA must pick a code on purpose instead of silently moving the learner.
    const hasCurrentCode = attendanceCodes.some(code => code.isCurrent);
    const codeCoursePeriodDate = attendanceCodes.find(code => code.id === codeId)?.coursePeriodDate || null;

    // Start editable only when the code has no preset period; end is always start + 1 year - 1 day.
    const startEditable = !codeCoursePeriodDate;
    const endDate = startDate ? moment(startDate).add(1, 'year').subtract(1, 'day').format(DATE_YMD_ISO) : '';

    // Default start: code's period, else the learner's registration date (学習者の登録日) — but never a
    // past date when the GA has to pick it manually (no preset code period), since the calendar below
    // blocks re-picking one anyway; defaulting to a stale registration date would silently submit a past
    // date if the GA never touches the field.
    const applyCode = (code) => {
        let defaultStart = code?.coursePeriodDate || student?.createdAt;
        if (!code?.coursePeriodDate && defaultStart && moment(defaultStart).isBefore(moment(), 'day')) {
            defaultStart = moment();
        }
        setCodeId(code?.id ?? '');
        setStartDate(defaultStart ? moment(defaultStart).format(DATE_YMD_ISO) : '');
        setStartError(false);
        setCodeError(false);
    };

    // Open on the learner's current code.
    useEffect(() => {
        if (mode === 'approve') {
            applyCode(attendanceCodes.find(code => code.isCurrent));
        }
    }, [mode, student, attendanceCodes]);

    if (!mode || !student) return null;

    const handleOk = () => {
        if (mode === 'approve') {
            // 受講コード / 受講開始日 are required — block submit and flag the empty field.
            if (codeId === '' || !startDate) {
                setCodeError(codeId === '');
                setStartError(!startDate);
                return;
            }
            onApprove({ coursePeriod: startDate, attendanceCodeId: codeId });
        } else {
            onReject();
        }
    };

    return (
        <div className='approval-modal-overlay'>
            <div className='approval-modal-box'>
                <div className='approval-modal-header'>ご確認</div>

                {mode === 'approve' && (
                    <div className='approval-modal-body'>
                        <div className='approval-modal-code-row'>
                            <label htmlFor='approvalAttendanceCode'>受講コード</label>
                            <div className='approval-modal-code-field'>
                                {/* Picking a code reloads 受講開始日 from that code's preset period. */}
                                <select
                                    id='approvalAttendanceCode'
                                    value={codeId}
                                    onChange={(e) => applyCode(attendanceCodes.find(code => String(code.id) === e.target.value))}
                                >
                                    {!hasCurrentCode && <option value='' disabled hidden>選択してください</option>}
                                    {attendanceCodes.map(code => (
                                        <option key={code.id} value={code.id}>{code.attendanceCode}</option>
                                    ))}
                                </select>
                                {/* Hint under the dropdown, per Figma (#18011). */}
                                <p className='approval-modal-code-note'>※承認前に、受講者の受講コードを別の受講コードに変更することができます。</p>
                            </div>
                        </div>
                        {/* No code at all: the current one was deleted and the group has no selectable code left. */}
                        {attendanceCodes.length === 0 ? (
                            <p className='approval-modal-required-error'>選択可能な受講コードがありません。</p>
                        ) : codeError && (
                            <p className='approval-modal-required-error'>{MSG['error.required']}</p>
                        )}
                        <div className='approval-modal-date-row'>
                            <label className='field-required'>受講開始日</label>
                            <CalendarCustom
                                date={startDate || null}
                                displayMode={'date'}
                                disabled={!startEditable}
                                minDate={new Date()}
                                removeIconCalendar={false}
                                onChange={(date) => {
                                    setStartDate(date ? moment(date).format(DATE_YMD_ISO) : '');
                                    setStartError(false);
                                }}
                            />
                            <span>~</span>
                            {/* end date is auto-derived and read-only */}
                            <CalendarCustom
                                date={endDate || null}
                                displayMode={'date'}
                                disabled
                                removeIconCalendar={false}
                            />
                        </div>
                        {startError && (
                            <p className='approval-modal-required-error'>{MSG['error.required']}</p>
                        )}
                        <p className='approval-modal-confirm-text approval-modal-confirm-text--danger'>
                            こちらの受講者の受講を承認されるということでよろしいですね？
                        </p>
                    </div>
                )}

                {mode === 'reject' && (
                    <div className='approval-modal-body'>
                        <p className='approval-modal-confirm-text'>
                            こちらの受講者の受講を承認されないということでよろしいですね？
                        </p>
                    </div>
                )}

                <div className='approval-modal-footer'>
                    <button type='button' className='btn btn-cancel-1' onClick={onCancel}>
                        キャンセル
                    </button>
                    <button type='button' className='btn btn-default-5' onClick={handleOk}>
                        OK
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ApprovalConfirmModal;
